import type Phaser from 'phaser';

/**
 * A single handle on the running game, published only in a development build.
 *
 * The browser suite has to drive the renderer — stand the entity somewhere and
 * look at what covers it — and a canvas offers no way in. Rather than sprinkle
 * bespoke test hooks through the scene, one reference is published and the
 * suite uses the renderer's own public API through it.
 *
 * The room handle beside it is the same idea for the half of the client that is
 * not a canvas: a second browser can only land in the *same* floor as the first
 * if somebody tells it the room id, and until there is a lobby (`TODO.md` #171)
 * the only thing that knows one is the client that created it. It carries no
 * capability — a room id is what a matchmaker would hand out anyway — and like
 * the game handle it does not exist in a production bundle.
 *
 * `import.meta.env.DEV` is false in `vite build`, so nothing of this ships.
 */
declare global {
  interface Window {
    /** The running Phaser game, in development builds only. Never present in production. */
    d2Game?: Phaser.Game;
    /** The room this client is in, in development builds only. Absent in the renderer sandbox. */
    d2Room?: D2RoomHandle;
  }
}

export interface D2RoomHandle {
  /** The room's id — what a second browser passes as `?room=` to join this same floor. */
  readonly roomId: string;
  /** This client's session id, which is also the key its own entity is drawn under. */
  readonly localId: string;
}

export const publishGameHandle = (game: Phaser.Game | null): void => {
  if (!import.meta.env.DEV) return;

  if (game === null) {
    delete window.d2Game;
  } else {
    window.d2Game = game;
  }
};

export const publishRoomHandle = (room: D2RoomHandle | null): void => {
  if (!import.meta.env.DEV) return;

  if (room === null) {
    delete window.d2Room;
  } else {
    window.d2Room = room;
  }
};
